import React, { useState, useEffect, useMemo, useRef } from 'react';
import { apiClient } from '../services/api';
import { Plus, Trash2, FileText, ArrowRight, Upload, X, Download, ClipboardList, Package, Search, MessageSquare, FileSpreadsheet, Edit2, Check, Printer, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const PurchaseRequisitions = () => {
  const navigate = useNavigate();
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [converting, setConverting] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState({ itemId: null, show: false });
  const [editingRemarks, setEditingRemarks] = useState(null);
  const [remarksText, setRemarksText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [prApprovalRequired, setPrApprovalRequired] = useState(false);
  const [approving, setApproving] = useState(null);
  const csvInputRef = useRef(null);
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.is_org_owner || 
    (currentUser.permissions || []).includes('manage_organization') ||
    (currentUser.role_names || []).includes('Super Admin');
  
  const [items, setItems] = useState([{
    id: `item_${Date.now()}`,
    item_name: '',
    description: '',
    quantity: '',
    unit: 'NOS',
    payment_term: '',
    transport: '',
    files: []
  }]);
  const [notes, setNotes] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    fetchPRs();
    fetchOrgSettings();
  }, []);

  const fetchOrgSettings = async () => {
    try {
      const response = await apiClient.get('/organization/settings');
      setPrApprovalRequired(response.data.pr_approval_required || false);
    } catch (e) {
      console.log('Could not fetch org settings');
    }
  };

  const fetchPRs = async () => {
    try {
      const response = await apiClient.get('/purchase-requisitions');
      setPrs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching PRs:', error);
      toast.error('Failed to load Purchase Requisitions');
      setLoading(false);
    }
  };

  // Filter PRs based on search query
  const filteredPRs = useMemo(() => {
    if (!searchQuery.trim()) return prs;
    const query = searchQuery.toLowerCase();
    return prs.filter(pr => {
      // Search in PR number
      if (pr.pr_number?.toLowerCase().includes(query)) return true;
      // Search in created by
      if (pr.created_by?.toLowerCase().includes(query)) return true;
      // Search in notes
      if (pr.notes?.toLowerCase().includes(query)) return true;
      // Search in items
      if (pr.items?.some(item => 
        item.item_name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      )) return true;
      return false;
    });
  }, [prs, searchQuery]);

  // Item name autocomplete
  const fetchItemSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setItemSuggestions([]);
      return;
    }
    try {
      const response = await apiClient.get(`/items/suggestions?q=${encodeURIComponent(query)}`);
      setItemSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleItemNameChange = (itemId, value) => {
    updateItem(itemId, { item_name: value });
    setShowSuggestions({ itemId, show: true });
    // Debounce the API call
    clearTimeout(window.prItemSuggestTimeout);
    window.prItemSuggestTimeout = setTimeout(() => {
      fetchItemSuggestions(value);
    }, 300);
  };

  const selectSuggestion = (itemId, suggestion) => {
    updateItem(itemId, {
      item_name: suggestion.item_name,
      description: suggestion.description || '',
      unit: suggestion.unit || 'NOS'
    });
    setShowSuggestions({ itemId: null, show: false });
    setItemSuggestions([]);
  };

  // CSV Upload Handler
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast.error('CSV file must have at least a header row and one data row');
          return;
        }

        // Parse header
        const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
        
        // Expected columns
        const nameIdx = headers.findIndex(h => h.includes('item') || h.includes('name'));
        const descIdx = headers.findIndex(h => h.includes('desc'));
        const qtyIdx = headers.findIndex(h => h.includes('qty') || h.includes('quantity'));
        const uomIdx = headers.findIndex(h => h.includes('uom') || h.includes('unit'));
        const paymentIdx = headers.findIndex(h => h.includes('payment'));
        const transportIdx = headers.findIndex(h => h.includes('transport'));

        if (nameIdx === -1) {
          toast.error('CSV must have an "item_name" column');
          return;
        }

        const newItems = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length === 0 || !values[nameIdx]) continue;

          newItems.push({
            id: `item_${Date.now()}_${i}`,
            item_name: values[nameIdx] || '',
            description: descIdx >= 0 ? values[descIdx] || '' : '',
            quantity: qtyIdx >= 0 ? values[qtyIdx] || '' : '',
            unit: uomIdx >= 0 ? values[uomIdx] || 'NOS' : 'NOS',
            payment_term: paymentIdx >= 0 ? values[paymentIdx] || '' : '',
            transport: transportIdx >= 0 ? values[transportIdx] || '' : '',
            files: []
          });
        }

        if (newItems.length === 0) {
          toast.error('No valid items found in CSV');
          return;
        }

        // Replace existing items with CSV items
        setItems(newItems);
        toast.success(`Loaded ${newItems.length} items from CSV`);
      } catch (error) {
        console.error('CSV parsing error:', error);
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  // Update PR remarks
  const handleUpdateRemarks = async (prId) => {
    try {
      await apiClient.patch(`/purchase-requisitions/${prId}/remarks`, { remarks: remarksText });
      toast.success('Remarks updated successfully');
      setEditingRemarks(null);
      fetchPRs();
    } catch (error) {
      console.error('Error updating remarks:', error);
      toast.error('Failed to update remarks');
    }
  };

  // Download PR as PDF
  const handleDownloadPDF = async (prId, prNumber) => {
    try {
      const response = await apiClient.get(`/purchase-requisitions/${prId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PR-${prNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Approve PR
  const handleApprovePR = async (prId) => {
    setApproving(prId);
    try {
      await apiClient.post(`/purchase-requisitions/${prId}/approve`);
      toast.success('PR approved successfully');
      fetchPRs();
    } catch (error) {
      console.error('Error approving PR:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve PR');
    } finally {
      setApproving(null);
    }
  };

  // Reject PR
  const handleRejectPR = async (prId) => {
    const reason = prompt('Enter rejection reason (optional):');
    setApproving(prId);
    try {
      await apiClient.post(`/purchase-requisitions/${prId}/reject?reason=${encodeURIComponent(reason || '')}`);
      toast.success('PR rejected');
      fetchPRs();
    } catch (error) {
      console.error('Error rejecting PR:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject PR');
    } finally {
      setApproving(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isCreating) return;

    // Validate at least one item has required fields
    const validItems = items.filter(item => item.item_name && item.quantity);
    if (validItems.length === 0) {
      toast.error('Please add at least one item with name and quantity');
      return;
    }

    setIsCreating(true);

    try {
      const response = await apiClient.post('/purchase-requisitions', {
        items: validItems.map(item => ({
          item_name: item.item_name,
          description: item.description,
          quantity: parseFloat(item.quantity),
          unit: item.unit || 'NOS',
          payment_term: item.payment_term,
          transport: item.transport
        })),
        notes,
        created_by: createdBy
      });

      const prId = response.data.id;

      // Upload any general pending files
      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          await apiClient.post(`/purchase-requisitions/${prId}/upload-attachment`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) {
          console.error('Error uploading file:', err);
        }
      }

      // Upload item-wise files
      for (let i = 0; i < validItems.length; i++) {
        const item = validItems[i];
        if (item.files && item.files.length > 0) {
          for (const file of item.files) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('item_index', i.toString());
            try {
              await apiClient.post(`/purchase-requisitions/${prId}/upload-attachment`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (err) {
              console.error('Error uploading item file:', err);
            }
          }
        }
      }

      toast.success(`Purchase Requisition ${response.data.pr_number} created successfully`);
      setShowDialog(false);
      resetForm();
      fetchPRs();
    } catch (error) {
      console.error('Error creating PR:', error);
      toast.error(error.response?.data?.detail || 'Failed to create Purchase Requisition');
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setItems([{
      id: `item_${Date.now()}`,
      item_name: '',
      description: '',
      quantity: '',
      unit: 'NOS',
      payment_term: '',
      transport: '',
      files: []
    }]);
    setNotes('');
    setCreatedBy('');
    setAttachments([]);
    setPendingFiles([]);
  };

  const addItem = () => {
    setItems([...items, {
      id: `item_${Date.now()}`,
      item_name: '',
      description: '',
      quantity: '',
      unit: 'NOS',
      payment_term: '',
      transport: '',
      files: []
    }]);
  };

  const updateItem = (id, updates) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, ...updates } : item
    ));
  };

  const removeItem = (id) => {
    if (items.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    setItems(items.filter(item => item.id !== id));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setPendingFiles([...pendingFiles, ...files]);
  };

  const removePendingFile = (index) => {
    setPendingFiles(pendingFiles.filter((_, i) => i !== index));
  };

  // Handle item-wise file selection
  const handleItemFileSelect = (itemId, e) => {
    const files = Array.from(e.target.files);
    setItems(items.map(item => {
      if (item.id === itemId) {
        return { ...item, files: [...(item.files || []), ...files] };
      }
      return item;
    }));
    e.target.value = ''; // Reset input
  };

  // Remove file from specific item
  const removeItemFile = (itemId, fileIndex) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const newFiles = [...(item.files || [])];
        newFiles.splice(fileIndex, 1);
        return { ...item, files: newFiles };
      }
      return item;
    }));
  };

  const handleDeletePR = async (prId, prNumber) => {
    if (!window.confirm(`Are you sure you want to delete ${prNumber}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(prId);
    try {
      await apiClient.delete(`/purchase-requisitions/${prId}`);
      toast.success(`${prNumber} deleted successfully`);
      fetchPRs();
    } catch (error) {
      console.error('Error deleting PR:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete PR');
    } finally {
      setDeleting(null);
    }
  };

  const handleConvertToRFQ = async (prId, prNumber) => {
    setConverting(prId);
    try {
      const response = await apiClient.post(`/purchase-requisitions/${prId}/convert-to-rfq`);
      const rfqData = response.data;

      // Navigate to RFQ creation page with pre-filled data
      navigate('/rfqs?create=true', { 
        state: { 
          prData: rfqData,
          prId: prId,
          prNumber: prNumber
        }
      });
    } catch (error) {
      console.error('Error converting PR to RFQ:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert PR to RFQ');
    } finally {
      setConverting(null);
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    resetForm();
    setIsCreating(false); // Reset creating state when dialog closes
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="pr-loading">
        Loading Purchase Requisitions...
      </div>
    );
  }

  return (
    <div data-testid="purchase-requisitions-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="pr-title">
            Purchase Requisitions
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Create purchase requests and convert them to RFQs
          </p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="create-pr-btn">
          <Plus size={18} className="mr-2" />
          Create PR
        </Button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by PR Number, Item Name, Created By..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="pr-search-input"
          />
        </div>
      </div>

      {/* PR List */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="pr-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  PR Number
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Remarks / Status Update
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredPRs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center" data-testid="no-pr-message">
                    <ClipboardList size={48} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 text-lg">
                      {searchQuery ? 'No PRs match your search' : 'No Purchase Requisitions yet'}
                    </p>
                    <p className="text-slate-400 text-sm mt-1">
                      {searchQuery ? 'Try a different search term' : 'Create a PR to request items for procurement'}
                    </p>
                    {!searchQuery && (
                      <Button 
                        className="mt-4" 
                        onClick={() => setShowDialog(true)}
                        data-testid="create-first-pr-btn"
                      >
                        <Plus size={16} className="mr-2" />
                        Create First PR
                      </Button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredPRs.map((pr) => (
                  <tr key={pr.id} className="hover:bg-slate-50" data-testid={`pr-row-${pr.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono font-medium text-slate-900">{pr.pr_number}</span>
                      {pr.attachments?.length > 0 && (
                        <span className="ml-2 text-xs text-slate-400">
                          ({pr.attachments.length} files)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {pr.items?.slice(0, 3).map((item, idx) => (
                          <div key={item.id || idx} className="text-sm">
                            <span className={`font-semibold ${idx === 0 ? 'text-blue-600' : 'text-green-600'}`}>
                              {idx + 1}.
                            </span>{' '}
                            <span className="text-slate-900">{item.item_name}</span>
                            <span className="text-slate-500 font-mono text-xs ml-2">
                              ({item.quantity} {item.unit})
                            </span>
                          </div>
                        ))}
                        {pr.items?.length > 3 && (
                          <span className="text-xs text-slate-400">
                            +{pr.items.length - 3} more items
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {pr.created_by || '-'}
                    </td>
                    {/* Remarks Column */}
                    <td className="px-4 py-4">
                      {editingRemarks === pr.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={remarksText}
                            onChange={(e) => setRemarksText(e.target.value)}
                            placeholder="Add remarks..."
                            className="text-sm h-8 w-40"
                            data-testid={`pr-remarks-input-${pr.id}`}
                          />
                          <button
                            onClick={() => handleUpdateRemarks(pr.id)}
                            className="text-green-600 hover:text-green-800"
                            data-testid={`pr-remarks-save-${pr.id}`}
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => setEditingRemarks(null)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600 max-w-[150px] truncate" title={pr.remarks || ''}>
                            {pr.remarks || <span className="text-slate-400 italic">No remarks</span>}
                          </span>
                          <button
                            onClick={() => {
                              setEditingRemarks(pr.id);
                              setRemarksText(pr.remarks || '');
                            }}
                            className="text-slate-400 hover:text-blue-600"
                            data-testid={`pr-remarks-edit-${pr.id}`}
                          >
                            <Edit2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          pr.status === 'converted'
                            ? 'bg-green-100 text-green-800'
                            : pr.status === 'approved'
                            ? 'bg-blue-100 text-blue-800'
                            : pr.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : prApprovalRequired
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                        data-testid={`pr-status-${pr.id}`}
                      >
                        {pr.status === 'converted' ? 'Converted to RFQ' : 
                         pr.status === 'approved' ? 'Approved' :
                         pr.status === 'rejected' ? 'Rejected' :
                         prApprovalRequired ? 'Pending Approval' : 'Pending'}
                      </span>
                      {pr.status === 'approved' && pr.approved_by_name && (
                        <div className="text-xs text-slate-500 mt-1">by {pr.approved_by_name}</div>
                      )}
                      {pr.status === 'rejected' && pr.rejection_reason && (
                        <div className="text-xs text-red-500 mt-1" title={pr.rejection_reason}>
                          {pr.rejection_reason.substring(0, 20)}...
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {new Date(pr.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* PDF Download - Always available */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPDF(pr.id, pr.pr_number)}
                          className="text-xs h-8"
                          title="Download PDF"
                          data-testid={`download-pdf-${pr.id}`}
                        >
                          <Printer size={14} />
                        </Button>
                        
                        {/* Approval buttons for Super Admin when approval is required */}
                        {prApprovalRequired && isSuperAdmin && (pr.status === 'pending' || pr.status === 'submitted') && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprovePR(pr.id)}
                              disabled={approving === pr.id}
                              className="text-xs h-8 bg-green-600 hover:bg-green-700"
                              title="Approve PR"
                              data-testid={`approve-pr-${pr.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              {approving === pr.id ? '...' : 'Approve'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectPR(pr.id)}
                              disabled={approving === pr.id}
                              className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
                              title="Reject PR"
                              data-testid={`reject-pr-${pr.id}`}
                            >
                              <XCircle size={14} />
                            </Button>
                          </>
                        )}
                        
                        {/* Convert to RFQ - Show only when approved or when approval not required */}
                        {(pr.status === 'approved' || (pr.status === 'pending' && !prApprovalRequired)) && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleConvertToRFQ(pr.id, pr.pr_number)}
                              disabled={converting === pr.id}
                              className="text-xs h-8"
                              data-testid={`convert-pr-${pr.id}`}
                            >
                              <ArrowRight size={14} className="mr-1" />
                              {converting === pr.id ? 'Converting...' : 'Convert to RFQ'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeletePR(pr.id, pr.pr_number)}
                              disabled={deleting === pr.id}
                              className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`delete-pr-${pr.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </>
                        )}
                        
                        {/* Delete for pending PRs when approval is required (non-super admin) */}
                        {prApprovalRequired && !isSuperAdmin && pr.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeletePR(pr.id, pr.pr_number)}
                            disabled={deleting === pr.id}
                            className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`delete-pr-${pr.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                        
                        {pr.status === 'converted' && pr.converted_to_rfq_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/rfqs/${pr.converted_to_rfq_id}`)}
                            className="text-xs h-8"
                            data-testid={`view-rfq-${pr.id}`}
                          >
                            <FileText size={14} className="mr-1" />
                            View RFQ
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create PR Dialog */}
      <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-pr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList size={20} />
              Create Purchase Requisition
            </DialogTitle>
            <DialogDescription>
              Request items for procurement. After creation, you can convert it to an RFQ.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Items Section */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold">Items</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{items.length} item(s)</span>
                  {/* CSV Upload */}
                  <input
                    type="file"
                    accept=".csv"
                    ref={csvInputRef}
                    onChange={handleCSVUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Download sample CSV
                      const sampleCSV = "item_name,description,qty,uom,payment_term,transport\nExample Item,Description here,100,NOS,Net 30,Ex-Works\nAnother Item,Another description,50,Kg,Advance,FOB";
                      const blob = new Blob([sampleCSV], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'pr_items_sample.csv';
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="text-xs h-7"
                    data-testid="download-pr-sample-csv"
                  >
                    <Download size={12} className="mr-1" />
                    Sample CSV
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => csvInputRef.current?.click()}
                    className="text-xs h-7"
                    data-testid="upload-pr-csv-btn"
                  >
                    <FileSpreadsheet size={12} className="mr-1" />
                    Upload CSV
                  </Button>
                </div>
              </div>

              {items.map((item, index) => (
                <div 
                  key={item.id} 
                  className={`p-4 border-2 rounded-lg ${
                    index === 0 ? 'bg-blue-50 border-blue-300' : 'bg-green-50 border-green-300'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <span className={`text-lg font-semibold ${
                      index === 0 ? 'text-blue-800' : 'text-green-800'
                    }`}>
                      <Package size={18} className="inline mr-2" />
                      Item #{index + 1}
                    </span>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <Input
                        placeholder="Item Name * (type to search history)"
                        value={item.item_name}
                        onChange={(e) => handleItemNameChange(item.id, e.target.value)}
                        onFocus={() => item.item_name.length >= 2 && setShowSuggestions({ itemId: item.id, show: true })}
                        onBlur={() => setTimeout(() => setShowSuggestions({ itemId: null, show: false }), 200)}
                        autoComplete="off"
                        required={index === 0}
                        className="bg-white"
                        data-testid={`pr-item-name-${index}`}
                      />
                      {showSuggestions.show && showSuggestions.itemId === item.id && itemSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {itemSuggestions.map((suggestion, idx) => (
                            <div
                              key={idx}
                              className="px-3 py-2 hover:bg-slate-100 cursor-pointer"
                              onMouseDown={() => selectSuggestion(item.id, suggestion)}
                            >
                              <div className="text-sm font-medium text-slate-900">{suggestion.item_name}</div>
                              <div className="text-xs text-slate-500">{suggestion.description} | {suggestion.unit}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem(item.id, { description: e.target.value })}
                      className="bg-white"
                      data-testid={`pr-item-desc-${index}`}
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Quantity *"
                      value={item.quantity}
                      onChange={(e) => updateItem(item.id, { quantity: e.target.value })}
                      required={index === 0}
                      className="bg-white"
                      data-testid={`pr-item-qty-${index}`}
                    />
                    <Input
                      placeholder="Unit (e.g., NOS, Kg, Pcs)"
                      value={item.unit}
                      onChange={(e) => updateItem(item.id, { unit: e.target.value })}
                      className="bg-white"
                      data-testid={`pr-item-unit-${index}`}
                    />
                    <Input
                      placeholder="Payment Term (optional)"
                      value={item.payment_term}
                      onChange={(e) => updateItem(item.id, { payment_term: e.target.value })}
                      className="bg-white"
                      data-testid={`pr-item-payment-${index}`}
                    />
                    <Input
                      placeholder="Transport (optional)"
                      value={item.transport}
                      onChange={(e) => updateItem(item.id, { transport: e.target.value })}
                      className="bg-white"
                      data-testid={`pr-item-transport-${index}`}
                    />
                  </div>
                  
                  {/* Item-wise Attachment */}
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        multiple
                        onChange={(e) => handleItemFileSelect(item.id, e)}
                        className="hidden"
                        id={`pr-item-file-${item.id}`}
                      />
                      <label
                        htmlFor={`pr-item-file-${item.id}`}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs border border-dashed border-slate-300 rounded cursor-pointer hover:bg-white transition-colors"
                      >
                        <Upload size={14} className="text-slate-400" />
                        <span className="text-slate-600">Attach Files for Item #{index + 1}</span>
                      </label>
                    </div>
                    {item.files && item.files.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {item.files.map((file, fileIdx) => (
                          <div
                            key={fileIdx}
                            className="flex items-center gap-1 px-2 py-1 bg-white rounded border text-xs"
                          >
                            <span className="text-slate-700 truncate max-w-[120px]">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeItemFile(item.id, fileIdx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addItem}
                  className="border-dashed border-2 border-green-400 text-green-700 hover:bg-green-50"
                  data-testid="add-pr-item-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Add Item #{items.length + 1}
                </Button>
              </div>
            </div>

            {/* General Attachments Section */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">General Attachments (Optional)</Label>
              <p className="text-xs text-slate-500 mb-3">
                Attach general specifications or reference documents for the entire PR
              </p>

              <div className="space-y-2">
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="pr-file-upload"
                />
                <label
                  htmlFor="pr-file-upload"
                  className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  <Upload size={18} className="text-slate-400" />
                  <span className="text-sm text-slate-600">Click to upload files</span>
                </label>

                {pendingFiles.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {pendingFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-slate-50 rounded border"
                      >
                        <span className="text-sm text-slate-700 truncate max-w-xs">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removePendingFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notes Section */}
            <div className="border-t pt-4">
              <Label htmlFor="notes" className="text-base font-semibold">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add any additional notes or instructions..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-2"
                rows={3}
                data-testid="pr-notes"
              />
            </div>

            {/* Created By */}
            <div className="border-t pt-4">
              <Label htmlFor="created_by" className="text-base font-semibold">
                Requested By
              </Label>
              <Input
                id="created_by"
                placeholder="Name of the person requesting (optional)"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                className="mt-2"
                data-testid="pr-created-by"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                disabled={isCreating}
                data-testid="cancel-pr-btn"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating} data-testid="save-pr-btn">
                {isCreating ? 'Creating PR...' : 'Create PR'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseRequisitions;
